import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import { uploadPhoto } from "../lib/upload";
import { DatePicker } from "./DatePicker";
import { personDisplayName } from "@family/shared";
import { RELATION_OPTIONS, edgeFor, type RelationChoice } from "../lib/relationEdge";
import type {
  CreatePersonRequest,
  Person,
  PersonsListResponse,
  Relationship,
  UpdatePersonRequest,
} from "@family/shared";

interface PersonFormProps {
  familySlug: string;
  person?: Person;
  onSaved?: (person: Person) => void;
  onCancel?: () => void;
}

type DatePrecision = "year" | "exact";

function parsePrecision(val: string | null | undefined): DatePrecision {
  if (!val) return "year";
  return val.length > 4 ? "exact" : "year";
}

export function PersonForm({
  familySlug,
  person,
  onSaved,
  onCancel,
}: PersonFormProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const isEdit = Boolean(person);

  const [givenName, setGivenName] = useState(person?.givenName ?? "");
  const [familyName, setFamilyName] = useState(person?.familyName ?? "");
  const [nickname, setNickname] = useState(person?.nickname ?? "");

  // Birth date
  const [birthPrecision, setBirthPrecision] = useState<DatePrecision>(
    parsePrecision(person?.birthDate),
  );
  const [birthValue, setBirthValue] = useState(person?.birthDate ?? "");

  // Alive / deceased
  const [isAlive, setIsAlive] = useState(!person?.deathDate);

  // Death date
  const [deathPrecision, setDeathPrecision] = useState<DatePrecision>(
    parsePrecision(person?.deathDate),
  );
  const [deathValue, setDeathValue] = useState(person?.deathDate ?? "");

  const [bio, setBio] = useState(person?.bio ?? "");
  const [coverPreview, setCoverPreview] = useState<string | null>(
    person?.coverUrl ?? null,
  );
  const [coverMediaId, setCoverMediaId] = useState<string | null>(
    person?.coverMediaId ?? null,
  );
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverProgress, setCoverProgress] = useState(0);
  const [coverError, setCoverError] = useState<string | null>(null);

  // Link to the tree, on create only. Editing relationships of an existing
  // person happens on their own page, where the current ones are visible.
  const [relChoice, setRelChoice] = useState<RelationChoice>("child");
  const [relPerson, setRelPerson] = useState<Person | null>(null);
  const [relSearch, setRelSearch] = useState("");
  const relResults = useQuery<PersonsListResponse>({
    queryKey: ["persons", familySlug, relSearch],
    queryFn: () =>
      apiFetch<PersonsListResponse>(
        `/families/${familySlug}/persons?q=${encodeURIComponent(relSearch)}`,
      ),
    enabled: !isEdit && relSearch.trim().length >= 2 && !relPerson,
  });
  // Survives a failed link: the person is already created by then, and
  // re-submitting must not make a second one.
  const createdRef = useRef<Person | null>(null);

  const save = useMutation<
    Person,
    Error,
    CreatePersonRequest | UpdatePersonRequest
  >({
    mutationFn: async (body) => {
      if (isEdit) {
        return apiFetch<Person>(
          `/families/${familySlug}/persons/${person!.id}`,
          { method: "PATCH", body: JSON.stringify(body) },
        );
      }
      // Reuse the person from a previous attempt whose link failed, so a
      // retry adds the missing edge instead of a duplicate person.
      const saved =
        createdRef.current ??
        (await apiFetch<Person>(`/families/${familySlug}/persons`, {
          method: "POST",
          body: JSON.stringify(body),
        }));
      createdRef.current = saved;
      if (relPerson) {
        await apiFetch<Relationship>(`/families/${familySlug}/relationships`, {
          method: "POST",
          body: JSON.stringify(edgeFor(relChoice, saved.id, relPerson.id)),
        });
      }
      return saved;
    },
    onSuccess: (saved) => {
      createdRef.current = null;
      void queryClient.invalidateQueries({ queryKey: ["persons", familySlug] });
      void queryClient.invalidateQueries({
        queryKey: ["relationships", familySlug],
      });
      if (isEdit && person) {
        void queryClient.invalidateQueries({
          queryKey: ["person", familySlug, person.id],
        });
      }
      onSaved?.(saved);
    },
  });

  async function onPickCover(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const localUrl = URL.createObjectURL(file);
    setCoverPreview(localUrl);
    setCoverUploading(true);
    setCoverProgress(0);
    setCoverError(null);
    try {
      const mediaId = await uploadPhoto(familySlug, file, {
        onProgress: setCoverProgress,
      });
      setCoverMediaId(mediaId);
    } catch (err) {
      setCoverError(err instanceof Error ? err.message : String(err));
      setCoverPreview(person?.coverUrl ?? null);
    } finally {
      setCoverUploading(false);
    }
  }

  function removeCover() {
    setCoverMediaId(null);
    setCoverPreview(null);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!givenName.trim()) return;
    if (coverUploading) return;
    const payload: CreatePersonRequest = {
      givenName: givenName.trim(),
      familyName: familyName.trim() || undefined,
      nickname: nickname.trim() || null,
      birthDate: birthValue || null,
      deathDate: isAlive ? null : deathValue || null,
      bio: bio.trim() || undefined,
      coverMediaId,
    };
    save.mutate(payload);
  }

  return (
    <form onSubmit={handleSubmit} className="card stack">
      <h3 style={{ fontSize: "1rem", margin: 0 }}>
        {isEdit ? "Edit person" : "Add person"}
      </h3>

      {/* Photo upload */}
      <div style={{ display: "flex", gap: "var(--s-3)", alignItems: "flex-start" }}>
        <div
          style={{
            width: 96, height: 96, flexShrink: 0, borderRadius: "50%",
            overflow: "hidden", background: "var(--c-bg-alt)",
            border: "1px solid var(--c-border)", position: "relative",
          }}
        >
          {coverPreview ? (
            <img
              src={coverPreview} alt=""
              style={{
                width: "100%", height: "100%", objectFit: "cover",
                display: "block", opacity: coverUploading ? 0.5 : 1,
              }}
              onContextMenu={(e) => e.preventDefault()} draggable={false}
            />
          ) : (
            <div style={{
              width: "100%", height: "100%", display: "flex",
              alignItems: "center", justifyContent: "center",
              color: "var(--c-text-subtle)", fontSize: "0.8rem",
            }}>
              No photo
            </div>
          )}
          {coverUploading && (
            <div style={{
              position: "absolute", inset: 0, display: "flex",
              alignItems: "center", justifyContent: "center",
              color: "#fff", fontWeight: 700,
              textShadow: "0 1px 2px rgba(0,0,0,0.8)",
            }}>
              {coverProgress}%
            </div>
          )}
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "var(--s-2)" }}>
          <input ref={fileInputRef} type="file" accept="image/*"
            onChange={onPickCover} style={{ display: "none" }} />
          <button type="button" className="btn-secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={coverUploading || save.isPending}>
            {coverUploading ? "Uploading…" : coverPreview ? "Replace photo" : "Upload photo"}
          </button>
          {coverPreview && !coverUploading && (
            <button type="button" className="btn-tertiary" onClick={removeCover}
              disabled={save.isPending}
              style={{ alignSelf: "flex-start", color: "var(--c-danger)" }}>
              Remove photo
            </button>
          )}
          {coverError && <p className="text-error" style={{ fontSize: "0.85rem" }}>{coverError}</p>}
        </div>
      </div>

      {/* Names */}
      <div className="grid-2col">
        <div>
          <label htmlFor="p-given">Given name *</label>
          <input id="p-given" type="text" value={givenName}
            onChange={(e) => setGivenName(e.target.value)}
            required disabled={save.isPending} />
        </div>
        <div>
          <label htmlFor="p-family">Family name</label>
          <input id="p-family" type="text" value={familyName}
            onChange={(e) => setFamilyName(e.target.value)}
            disabled={save.isPending} />
        </div>
      </div>

      {/* Link to the tree. Sits directly under the names because that is
          where it gets seen: when it lived behind a separate "+ Add
          relationship" button on the tree page, whole sessions of people
          were added without ever being connected. */}
      {!isEdit && (
        <div
          className="card"
          style={{
            padding: "12px 14px",
            background: "var(--c-bg-alt)",
            display: "grid",
            gap: 8,
          }}
        >
          <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>
            How are they related?
          </div>
          <div
            style={{
              display: "flex",
              gap: "var(--s-2)",
              alignItems: "center",
              flexWrap: "wrap",
              fontSize: "0.9rem",
            }}
          >
            <span>This person is the</span>
            <select
              className="select-skapa"
              aria-label="Relationship to someone already in the tree"
              value={relChoice}
              onChange={(e) => setRelChoice(e.target.value as RelationChoice)}
              disabled={save.isPending}
              style={{ width: "auto", minHeight: 34, padding: "4px 10px" }}
            >
              {RELATION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {relPerson ? (
              <>
                <strong>{personDisplayName(relPerson)}</strong>
                <button
                  type="button"
                  className="btn-tertiary"
                  onClick={() => {
                    setRelPerson(null);
                    setRelSearch("");
                  }}
                  disabled={save.isPending}
                  style={{ padding: "2px 8px", fontSize: "0.8rem", minHeight: 0 }}
                >
                  Change
                </button>
              </>
            ) : (
              <input
                type="text"
                value={relSearch}
                onChange={(e) => setRelSearch(e.target.value)}
                placeholder="Search someone in the tree…"
                aria-label="Search someone already in the tree"
                disabled={save.isPending}
                style={{
                  minHeight: 34,
                  fontSize: "0.9rem",
                  padding: "4px 10px",
                  width: "auto",
                  flex: "0 1 240px",
                }}
              />
            )}
          </div>

          {!relPerson && (relResults.data?.persons ?? []).length > 0 && (
            <div style={{ display: "flex", gap: "var(--s-2)", flexWrap: "wrap" }}>
              {relResults.data!.persons.slice(0, 6).map((cand) => (
                <button
                  key={cand.id}
                  type="button"
                  className="tree-chip"
                  onClick={() => setRelPerson(cand)}
                  disabled={save.isPending}
                  style={{
                    background: "transparent",
                    color: "var(--c-text)",
                    border: "1px solid var(--c-border-strong)",
                  }}
                >
                  {personDisplayName(cand)}
                  {cand.birthDate ? ` (${cand.birthDate.slice(0, 4)})` : ""}
                </button>
              ))}
            </div>
          )}

          {!relPerson && (
            <p className="text-subtle" style={{ margin: 0, fontSize: "0.85rem" }}>
              Optional, but without it they will not show up in the family tree
              at all — only in search and photo tags. You can link them later
              from Tree audit.
            </p>
          )}
        </div>
      )}

      {/* Nickname */}
      <div>
        <label htmlFor="p-nickname">Nickname</label>
        <input id="p-nickname" type="text" value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          placeholder="What the family actually calls them"
          disabled={save.isPending} />
      </div>

      {/* Birth date */}
      <DatePicker
        label="Date of birth"
        precision={birthPrecision}
        onPrecisionChange={setBirthPrecision}
        value={birthValue}
        onChange={setBirthValue}
        disabled={save.isPending}
      />

      {/* Alive / Deceased toggle */}
      <div style={{ display: "flex", alignItems: "center", gap: "var(--s-3)" }}>
        <span style={{ fontWeight: 600 }}>Status</span>
        <label style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          cursor: "pointer", fontSize: "0.95rem",
        }}>
          <input type="radio" name="alive" checked={isAlive}
            onChange={() => setIsAlive(true)} disabled={save.isPending} />
          Alive
        </label>
        <label style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          cursor: "pointer", fontSize: "0.95rem",
        }}>
          <input type="radio" name="alive" checked={!isAlive}
            onChange={() => setIsAlive(false)} disabled={save.isPending} />
          Deceased
        </label>
      </div>

      {/* Death date */}
      {!isAlive && (
        <DatePicker
          label="Date of death"
          precision={deathPrecision}
          onPrecisionChange={setDeathPrecision}
          value={deathValue}
          onChange={setDeathValue}
          disabled={save.isPending}
        />
      )}

      {/* Bio */}
      <div>
        <label htmlFor="p-bio">Bio</label>
        <textarea id="p-bio" value={bio}
          onChange={(e) => setBio(e.target.value)}
          disabled={save.isPending} rows={3}
          style={{
            width: "100%", background: "var(--c-bg)", color: "var(--c-text)",
            border: "1px solid var(--c-border-strong)", borderRadius: 4,
            padding: "14px 16px", fontSize: "1rem", fontFamily: "inherit",
            resize: "vertical",
          }} />
      </div>

      {save.error && (
        <p className="text-error">
          {createdRef.current
            ? `${personDisplayName(createdRef.current)} was added, but linking them failed: ${save.error.message} — try again to add just the link.`
            : save.error.message}
        </p>
      )}

      <div style={{ display: "flex", gap: "var(--s-3)" }}>
        <button type="submit"
          disabled={save.isPending || !givenName.trim() || coverUploading}>
          {save.isPending
            ? isEdit
              ? "Saving…"
              : createdRef.current
                ? "Linking…"
                : "Adding…"
            : isEdit
              ? "Save changes"
              : createdRef.current
                ? "Retry link"
                : relPerson
                  ? "Add and link"
                  : "Add person"}
        </button>
        {onCancel && (
          <button type="button" className="btn-secondary"
            onClick={onCancel} disabled={save.isPending}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
